import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  BottomSheet,
  Button,
  Fab,
  List,
  ListItem,
  Notice,
  Skeleton,
  StateScreen,
  TextField,
  iconAdd,
} from '@sauda/ui/kit';
import { Screen, useChrome } from '../app/chrome.js';
import { useAuth } from '../auth/AuthContext.js';
import { api } from '../lib/api.js';
import { initials } from '../lib/initials.js';
import type { PartnerView } from '../lib/types.js';

/** Registration partners: the people who book the registration visit and upload the registered copy. */
export function PartnersScreen() {
  const { t } = useTranslation();
  const auth = useAuth();
  const chrome = useChrome();
  const cache = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [area, setArea] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = useQuery({
    queryKey: ['partners', auth.agencyId],
    queryFn: () => api<PartnerView[]>('/partners'),
  });
  const refresh = () => cache.invalidateQueries({ queryKey: ['partners'] });
  const create = useMutation({
    mutationFn: () =>
      api('/partners', {
        method: 'POST',
        body: {
          name: name.trim(),
          phone: phone.trim(),
          ...(area.trim() === '' ? {} : { area: area.trim() }),
        },
      }),
    onSuccess: async () => {
      setName('');
      setPhone('');
      setArea('');
      setError(null);
      setOpen(false);
      await refresh();
    },
    onError: () => setError(t('partners.failed')),
  });
  const toggle = useMutation({
    mutationFn: (v: { id: string; active: boolean }) =>
      api(`/partners/${v.id}`, { method: 'PATCH', body: { active: v.active } }),
    onSuccess: refresh,
    onError: () => setError(t('partners.failed')),
  });
  const canAdd = auth.allowed('partners.manage') && !chrome.readOnly;
  return (
    <Screen title={t('partners.title')} large eyebrow={auth.current?.agencyName}>
      {error !== null && !open ? <Notice tone="error">{error}</Notice> : null}
      {list.isPending ? <Skeleton height={72} /> : null}
      {list.isSuccess && list.data.length === 0 ? (
        <StateScreen kind="empty" compact title={t('partners.none')} />
      ) : null}
      {list.data === undefined || list.data.length === 0 ? null : (
        <List label={t('partners.title')}>
          {list.data.map((p) => (
            <ListItem
              key={p.id}
              title={p.name}
              leading={<span className="row-avatar k-t-label-lg">{initials(p.name)}</span>}
              supporting={[p.phone, p.area, p.active ? null : t('partners.inactive')]
                .filter(Boolean)
                .join(' · ')}
              trailing={
                canAdd ? (
                  <Button
                    className="row-action"
                    variant="text"
                    onClick={() => toggle.mutate({ id: p.id, active: !p.active })}
                  >
                    {p.active ? t('partners.deactivate') : t('partners.activate')}
                  </Button>
                ) : undefined
              }
            />
          ))}
        </List>
      )}
      {canAdd ? (
        <div className="fab-slot">
          <Fab icon={iconAdd} label={t('partners.add')} extended onClick={() => setOpen(true)} />
        </div>
      ) : null}
      <BottomSheet
        open={open}
        onClose={() => setOpen(false)}
        label={t('partners.add')}
        title={t('partners.add')}
        closeLabel={t('app.close')}
        actions={
          <Button
            size="lg"
            block
            loading={create.isPending}
            disabled={name.trim().length < 2 || phone.trim() === ''}
            onClick={() => create.mutate()}
          >
            {t('partners.add')}
          </Button>
        }
      >
        <div className="form-stack">
          {error === null ? null : <Notice tone="error">{error}</Notice>}
          <TextField
            label={t('partners.name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            label={t('partners.phone')}
            type="tel"
            inputMode="numeric"
            prefix="+91"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <TextField
            label={t('partners.area')}
            value={area}
            onChange={(e) => setArea(e.target.value)}
          />
        </div>
      </BottomSheet>
    </Screen>
  );
}
